import { ENV } from './env.js';
import { getAccess, getRefresh, saveTokens, clearTokens } from './token.js';

/* Normalized API error. mia-data errors are RFC7807-ish:
   {type:"urn:mia:error:…", title, status, detail} → we map to a
   stable `code` the UI can branch on. */
export class ApiError extends Error {
  constructor(code, message, status = 0) {
    super(message || code);
    this.code = code; // unauthorized | forbidden | not_found | conflict | invalid | rate_limited | network | server | popup_blocked | cancelled | reauth_required
    this.status = status;
    this.type = ''; // server error `type` urn, when present
  }
}
const codeOf = (status) =>
  ({
    401: 'unauthorized',
    403: 'forbidden',
    404: 'not_found',
    409: 'conflict',
    422: 'invalid',
    429: 'rate_limited',
  })[status] || 'server';

async function rawFetch(path, { method = 'GET', body, auth = true } = {}) {
  const headers = { Accept: 'application/json' };
  if (body) headers['Content-Type'] = 'application/json';
  if (auth && getAccess()) headers['Authorization'] = 'Bearer ' + getAccess();
  let res;
  try {
    res = await fetch(ENV.API + path, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('network', 'network error — is the API reachable?');
  }
  let data = null;
  try {
    data = await res.json();
  } catch {
    /* empty body is fine */
  }
  if (!res.ok) {
    const err = new ApiError(codeOf(res.status), data?.detail || data?.title || `HTTP ${res.status}`, res.status);
    err.type = data?.type || '';
    throw err;
  }
  return data;
}

/* ---- refresh rotation, single-flight (per doc §2) ----
   access expires in ~30 min; on 401 refresh ONCE (dedup concurrent
   401s), replace BOTH tokens, retry the request. Refresh 401 →
   session dead → clear + surface `unauthorized`. */
let refreshing = null;
function refreshOnce() {
  if (!refreshing) {
    refreshing = (async () => {
      const rt = getRefresh();
      if (!rt) throw new ApiError('unauthorized', 'no session', 401);
      try {
        const d = await rawFetch('/client/auth/refresh', {
          method: 'POST',
          body: { refresh_token: rt },
          auth: false,
        });
        saveTokens(d);
      } catch (e) {
        /* only a refusal ends the session (the refresh token is dead or rejected); a network error or a 5xx is the
           server's bad moment, not the listener's — rethrown as it is, tokens kept (review 2026-09-21) */
        if (e && (e.status === 400 || e.status === 401 || e.status === 403)) {
          clearTokens();
          throw new ApiError('unauthorized', 'session expired — sign in again', 401);
        }
        throw e;
      }
    })().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

/* 401 detail rules (doc §0): ONLY "Access token expired" goes through
   refresh. Everything else — missing/invalid token, dead refresh,
   deleted account ("User no longer exists"), suspended account —
   clears local state immediately. Refreshing on a deleted account
   401s forever and reads as a frozen page, hence the hard rule. */
export async function request(path, opts = {}) {
  /* a refresh token alone (the pass link's dead-token sweep left it, or an access token that never landed): the door
     counts it as a session, but a bare request only earns "Missing bearer token" and a wipe — mint the access first */
  if (opts.auth !== false && !getAccess() && getRefresh()) await refreshOnce();
  try {
    return await rawFetch(path, opts);
  } catch (e) {
    if (e.code === 'unauthorized' && opts.auth !== false) {
      if (/access token expired/i.test(e.message) && getRefresh()) {
        await refreshOnce(); // throws `unauthorized` if the session is dead
        return rawFetch(path, opts); // retry once with the new access token
      }
      clearTokens(); // deleted/suspended/broken — never retry
    }
    throw e;
  }
}
export { rawFetch };
